import { HttpErrorResponse } from '@angular/common/http';
import { inject } from '@angular/core';
import { tapResponse } from '@ngrx/operators';
import { patchState, signalStore, withMethods } from '@ngrx/signals';
import {
  addEntity,
  removeEntity,
  setAllEntities,
  updateEntity,
  upsertEntities,
  withEntities,
} from '@ngrx/signals/entities';
import { rxMethod } from '@ngrx/signals/rxjs-interop';
import { ToastrErrorComponent } from '@shared/components/toastr/toastr-error.component';
import { toast } from 'ngx-sonner';
import { pipe, switchMap } from 'rxjs';
import { v7 as uuidv7 } from 'uuid';

import { Response } from '../../../../shared/models/response.model';
import { withSelectedEntity } from '../../../../shared/store/selected-entity.feature';
import { IndicateurPerformance } from '../models/indicateur-performance';
import { IndicateurPerformanceService } from '../processus-indicateur-performance.service';
import { IndicateurCreateRequest } from '../requests/create-request';
import { IndicateurUpdateRequest } from '../requests/update-request';

export const IndicateurPerformanceStore = signalStore(
  { providedIn: 'root' },
  withEntities<IndicateurPerformance>(),
  withSelectedEntity(),
  withMethods((store) => ({
    chargerIndicateurs: (state: { indicateurs: IndicateurPerformance[] }) =>
      patchState(store, setAllEntities(state.indicateurs)),
    ajouterIndicateur: (indicateur: IndicateurPerformance) => patchState(store, addEntity(indicateur)),
    ajouterIndicateurs: (indicateurs: IndicateurPerformance[]) => patchState(store, upsertEntities(indicateurs)),
    modifierIndicateur: (id: string, indicateur: Partial<IndicateurPerformance>) =>
      patchState(store, updateEntity({ id, changes: indicateur })),
    supprimerIndicateur: (id: string) => patchState(store, removeEntity(id)),
    selectionerIndicateur: (state: { idIndicateur: string }) =>
      patchState(store, { selectedEntityId: state.idIndicateur }),
    deselectionerIndicateur: () => patchState(store, { selectedEntityId: null }),
  })),
  withMethods((store, indicateurPerformanceService = inject(IndicateurPerformanceService)) => ({
    charger: rxMethod<string>(
      pipe(
        switchMap((référentielId) =>
          indicateurPerformanceService.trouverTous(référentielId).pipe(
            tapResponse({
              next: (result: Response<IndicateurPerformance>) =>
                store.chargerIndicateurs({ indicateurs: result.items }),
              error: (err) => {
                console.error(err);
                const toastrId = uuidv7();
                toast.custom(ToastrErrorComponent, {
                  componentProps: {
                    message: `Un problème est survenu lors de la récupération des indicateurs de performance, contactez le support.`,
                    id: toastrId,
                  },
                  id: toastrId,
                });
              },
            }),
          ),
        ),
      ),
    ),
    créer: rxMethod<{ data: IndicateurCreateRequest; next: () => void }>(
      pipe(
        switchMap(({ data, next }) =>
          indicateurPerformanceService.créer(data).pipe(
            tapResponse({
              next: (results: IndicateurPerformance[]) => {
                store.ajouterIndicateurs(results);
                next();
              },
              error: (err: HttpErrorResponse) => {
                console.error(err);
                const toastrId = uuidv7();
                toast.custom(ToastrErrorComponent, {
                  componentProps: {
                    message:
                      err.error.detail ??
                      `Un problème est survenu lors de la création des indicateurs de performance, contactez le support.`,
                    id: toastrId,
                  },
                  id: toastrId,
                });
              },
            }),
          ),
        ),
      ),
    ),
    modifier: rxMethod<{
      data: IndicateurUpdateRequest;
      next: () => void;
    }>(
      pipe(
        switchMap(({ data, next }) =>
          indicateurPerformanceService.modifier(data).pipe(
            tapResponse({
              next: () => {
                store.ajouterIndicateurs(data.indicateurs);
                next();
              },
              error: (err: HttpErrorResponse) => {
                console.error(err);
                const toastrId = uuidv7();
                toast.custom(ToastrErrorComponent, {
                  componentProps: {
                    message:
                      err.error.detail ??
                      `Un problème est survenu lors de la modification de l'indicateur de performance, contactez le support.`,
                    id: toastrId,
                  },
                  id: toastrId,
                });
              },
            }),
          ),
        ),
      ),
    ),
    supprimer: rxMethod<{
      idIndicateur: string;
      next: () => void;
    }>(
      pipe(
        switchMap(({ idIndicateur, next }) =>
          indicateurPerformanceService.supprimer(idIndicateur).pipe(
            tapResponse({
              next: () => next(),
              error: (err) => {
                console.error(err);
                const toastrId = uuidv7();
                toast.custom(ToastrErrorComponent, {
                  componentProps: {
                    message: `Un problème est survenu lors de la suppression de l'indicateur de performance, contactez le support.`,
                    id: toastrId,
                  },
                  id: toastrId,
                });
              },
            }),
          ),
        ),
      ),
    ),
    selection: rxMethod<string>(
      pipe(
        switchMap((idIndicateur) => indicateurPerformanceService.trouverParId(idIndicateur)),
        tapResponse({
          next: (indicateur) => store.ajouterIndicateur(indicateur),
          error: (err) => {
            console.error(err);
            const toastrId = uuidv7();
            toast.custom(ToastrErrorComponent, {
              componentProps: {
                message: `Un problème est survenu lors de la récupération de l'indicateur de performance, contactez le support.`,
                id: toastrId,
              },
              id: toastrId,
            });
          },
        }),
      ),
    ),
  })),
);
